import React, { useContext, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { LoadingV2 } from "@ucl/library";
import { UserContext } from "@/contexts/UserContext/UserContext";
import { onAuthenticationSuccess } from "./useLoginCallback";
import { BoltApiClientProvider } from "@bolt/core";
import variables from "@/config/variables";
import { useAuthHeadersRequired } from "./authUtils";

type RequireAuthProps = {
  children?: React.ReactNode;
  clientType: "consumer" | "banker";
};

export const RequireAuth: React.FC<RequireAuthProps> = ({ children, clientType }) => {
  const getAuthRequestHeader = useAuthHeadersRequired();
  const {
    isAuthenticated,
    isLoading,
    error,
    loginWithRedirect,
    getAccessTokenSilently,
  } = useAuth0();
  const { login, user: appUser, accessToken } = useContext(UserContext);

  useEffect(() => {
    if (isAuthenticated && !accessToken) {
      getAccessTokenSilently()
        .then((token) => {
          onAuthenticationSuccess(token, login, clientType);
        })
        .catch((e) => {
          if (e.error === "login_required" || e.error === "consent_required") {
            loginWithRedirect({
              appState: {
                returnTo: `${window.location.pathname}${window.location.search}`,
                clientType,
              },
            });
          } else {
            console.error("Error getting access token:", e);
          }
        });
    }
  }, [isAuthenticated, accessToken, getAccessTokenSilently, login, clientType, loginWithRedirect]);

  if (isLoading) {
    return <LoadingV2 loadingType="spinner" />;
  }

  if (error) {
    console.error("Auth0 error:", error);
    return (
      <div className="auth-error">
        <h2>Authentication Error</h2>
        <p>There was an issue with authentication. Please try again.</p>
        <button onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  if (!isAuthenticated) {
    loginWithRedirect({
      appState: {
        returnTo: `${window.location.pathname}${window.location.search}`,
        clientType,
      },
    });
    return <LoadingV2 loadingType="spinner" />;
  }

  // Wait for token and user context to be ready
  if (!accessToken || !appUser) {
    return <LoadingV2 loadingType="spinner" />;
  }

  return (
    <BoltApiClientProvider 
      baseUrl={variables.apiBaseUrl} 
      getHeaders={getAuthRequestHeader()}
    >
      {children}
    </BoltApiClientProvider>
  );
}; 